// Copyright © 2024 Ory Corp
// SPDX-License-Identifier: Apache-2.0

import { RecoveryFlow, UpdateRecoveryFlowBody } from '@ory/client'
import { UserAuthCard } from '@ory/elements'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sdk, useSdkError } from '../services/sdk'
import AuthLayout from '../components/layout/AuthLayout'
import { SpinnerIcon } from '@sistemiv/s-components'

export const Recovery = () => {
  const [flow, setFlow] = useState<RecoveryFlow | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const navigate = useNavigate()

  const getFlow = useCallback(
    (flowId: string) =>
      sdk
        .getRecoveryFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    // eslint-disable-next-line
    [],
  )

  // initialize the sdkError for generic handling of errors
  const sdkErrorHandler = useSdkError(getFlow, setFlow, '/recovery')

  // create a new recovery flow
  const createFlow = React.useCallback(() => {
    sdk
      .createBrowserRecoveryFlow()
      // flow contains the form fields, error messages and csrf token
      .then(({ data: flow }) => {
        // Update URI query params to include flow id
        setSearchParams({ ['flow']: flow.id }, { replace: true })
        // Set the flow data
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }, [sdkErrorHandler, setSearchParams, setFlow])

  const submitFlow = async (body: UpdateRecoveryFlowBody) => {
    if (!flow) return navigate('/login', { replace: true })

    try {
      const { data: updatedFlow } = await sdk.updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      })

      // Ako je prošao challenge, dobijamo novi ekran sa poljem za novu lozinku
      if (updatedFlow.state === 'passed_challenge') {
        const continueWith = updatedFlow.continue_with?.find((item) => item.action === 'show_settings_ui')
        // @ts-ignore
        if (continueWith?.flow?.url) {
          // @ts-ignore
          const settingsFlowUrl = new URL(continueWith.flow.url)
          const flowId = settingsFlowUrl.searchParams.get('flow')

          // Navigiraj na stranicu za podešavanja (gde će biti forma za novu lozinku)
          navigate(`/settings?flow=${flowId}`)
          return
        }
      } else {
        setFlow(updatedFlow)
      }
    } catch (err) {
      // sdkErrorHandler(err)
    }
  }

  useEffect(() => {
    if (!flow) {
      // we might redirect to this page after the flow is initialized, so we check for the flowId in the URL
      const flowId = searchParams.get('flow')
      // the flow already exists
      if (flowId) {
        getFlow(flowId).catch(createFlow) // if for some reason the flow has expired, we need to get a new one
        return
      }
      // we assume there was no flow, so we create a new one
      createFlow()
    }
  }, [flow, createFlow, getFlow, searchParams])

  return (
    <AuthLayout>
      {flow ? (
        <UserAuthCard
          flowType='recovery'
          className='custom-auth-card !min-w-[calc(100vw-4rem)] sm:!min-w-[400px] p-4 sm:p-6 rounded-md sm:rounded-lg'
          flow={flow}
          additionalProps={{
            loginURL: {
              handler: () => {
                navigate(
                  {
                    pathname: '/login',
                  },
                  { replace: true },
                )
              },
            },
          }}
          onSubmit={({ body }) => submitFlow(body as UpdateRecoveryFlowBody)}
        />
      ) : (
        <SpinnerIcon className='w-6 h-6 text-sky-500 animate-spin' />
      )}
    </AuthLayout>
  )
}
